import { SITE_NAME } from './buildSeoHead';
import { SITE_URL } from './siteUrl';

/** Wraps a JSON-LD object into the `scripts` entry shape TanStack Router's head() expects. */
export function jsonLdScript(schema) {
  return { type: 'application/ld+json', children: JSON.stringify(schema) };
}

// Source of truth for these URLs is django-constance config (SOCIAL_* keys,
// see src/apps/utils/views/social_links.py / the seed migration) -- update
// both places together if they ever change.
const SAME_AS = [
  'https://www.linkedin.com/company/secureonelabs/',
  'https://x.com/secureonelabs',
  'https://github.com/secureonelabs',
  'https://www.instagram.com/secureonelabs/',
  'https://www.youtube.com/@secureonelabs',
  'https://www.tiktok.com/@secureonelabs',
  // Entity profiles (not footer links, so not in constance): help engines tie the brand to one organization.
  'https://www.crunchbase.com/organization/secureonelabs',
  'https://theorg.com/org/secureonelabs',
];

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'SecureOneLabs',
    url: SITE_URL,
    // icon_logo.png ships from public/, which Vite serves under its `base`
    // prefix ('/static/dist/' in the shipped build -- see vite.config.js).
    // Hand-building `${SITE_URL}/icon_logo.png` dropped that prefix and 404'd
    // in production, leaving the Organization schema with no resolvable logo.
    // BASE_URL is Vite's documented way to address public/ assets from JS.
    logo: new URL(`${import.meta.env.BASE_URL}icon_logo.png`, SITE_URL).href,
    sameAs: SAME_AS,
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@secureonelabs.com',
      contactType: 'customer service',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '701 Tillery Street #12',
      addressLocality: 'Austin',
      addressRegion: 'TX',
      postalCode: '78702',
      addressCountry: 'US',
    },
  };
}

export function buildSoftwareApplicationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'BOSSS',
    applicationCategory: 'SecurityApplication',
    operatingSystem: 'Web',
    url: SITE_URL,
    description: 'Unified GRC, IAM, CMDB, XDR, and AI-agent security platform that replaces 8-12 disconnected security tools with one system.',
    publisher: {
      '@type': 'Organization',
      name: 'SecureOneLabs',
    },
  };
}

/**
 * FAQPage schema for site-authored FAQs. Google restricted FAQ rich results to
 * government/health sites in 2023, so this is for AI/LLM extraction, not SERP
 * stars. Not QAPage: that type is for forum-style Q&A with user-posted answers
 * and Search Console flags it as invalid without answerCount/author/url.
 * @param {Array<{q: string, a: string}>} qa
 */
export function buildFaqPageSchema(qa) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qa.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}

/**
 * @param {Array<{name: string, path: string}>} crumbs - root-first, e.g.
 *   [{name: 'Home', path: '/'}, {name: 'Platform', path: '/platform'}, {name: 'GRC', path: '/platform/grc'}]
 */
export function buildBreadcrumbSchema(crumbs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${SITE_URL}${crumb.path}`,
    })),
  };
}

/**
 * WebPage node carrying the visible "Updated" date as dateModified (SEO-84).
 * @param {{path: string, title: string, description: string, updated?: string}} route
 */
export function buildWebPageSchema({ path, title, description, updated }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: `${SITE_URL}${path}`,
    name: `${title} · ${SITE_NAME}`,
    description,
    ...(updated ? { dateModified: updated } : {}),
    isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: SITE_URL },
  };
}
